/**
 * Modal "Oznacz jako zapłacony" (kontrakt POST /external-costs/<id>/oznacz-zaplacony {sposob:'kw'|'konto', data, typ_platnosci}).
 * 'kw' = KW z kasy sklepu (numer z bramy numeracji, jak dotychczasowy /pay) - tylko gdy koszt ma sklep;
 * 'konto' = zaplacone z konta firmowego poza kasa, bez KW (nowa opcja - decyzja domyslna 2026-09-13).
 */
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { externalCostsService } from '../../services/externalCostsService';
import { formatujKwote, dzisISO, komunikatBledu, SPOSOBY_ZAPLATY } from './format';

const pole = { width: '100%', padding: '6px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)', boxSizing: 'border-box' };
const btn = (aktywny) => ({
  padding: '6px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '4px', cursor: 'pointer',
  border: `1px solid ${aktywny ? 'var(--success)' : 'var(--text-muted)'}`,
  background: aktywny ? 'var(--success)' : 'var(--card-bg)', color: aktywny ? '#fff' : 'var(--text-muted)',
});

export default function OznaczZaplaconyModal({ koszt, onZamknij, onZaplacono }) {
  const maSklep = Boolean(koszt?.location_id);
  const [sposob, setSposob] = useState(maSklep ? 'kw' : 'konto');
  const [data, setData] = useState(dzisISO());
  const [typPlatnosci, setTypPlatnosci] = useState('gotowka');
  const [wysylanie, setWysylanie] = useState(false);
  const [blad, setBlad] = useState(null);

  const zapisz = async (e) => {
    e.preventDefault();
    setWysylanie(true);
    setBlad(null);
    try {
      const body = { sposob, data };
      if (sposob === 'kw') body.typ_platnosci = typPlatnosci;
      const odp = await externalCostsService.oznaczZaplacony(koszt.id, body);
      const w = odp?.data || odp;
      toast.success(sposob === 'kw' ? `Zapłacono — KW nr ${w.kasa_operacja_id ? `#${w.kasa_operacja_id}` : ''} w kasie sklepu` : 'Oznaczono jako zapłacone z konta firmowego');
      if (onZaplacono) onZaplacono(w, koszt);
      if (onZamknij) onZamknij();
    } catch (err) {
      if (err?.isAuthError) return;
      setBlad(komunikatBledu(err, 'Nie udało się oznaczyć zapłaty.'));
    } finally {
      setWysylanie(false);
    }
  };

  const opcja = (id) => (
    <label key={id} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', padding: '8px 10px', border: `1px solid ${sposob === id ? 'var(--primary)' : 'var(--border)'}`, borderRadius: '6px', cursor: (id === 'kw' && !maSklep) ? 'not-allowed' : 'pointer', opacity: (id === 'kw' && !maSklep) ? 0.5 : 1 }}>
      <input type="radio" data-testid={`zaplac-sposob-${id}`} name="sposob" value={id} checked={sposob === id} disabled={id === 'kw' && !maSklep} onChange={() => setSposob(id)} />
      <span>
        <div style={{ fontSize: '12px', fontWeight: 600 }}>{SPOSOBY_ZAPLATY[id].etykieta}</div>
        <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{SPOSOBY_ZAPLATY[id].opis}{id === 'kw' && !maSklep ? ' — koszt ogólny nie ma kasy sklepu' : ''}</div>
      </span>
    </label>
  );

  return (
    <div data-testid="zaplac-modal" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--card-bg)', padding: '20px', borderRadius: '8px', maxWidth: '460px', width: '94%', border: '1px solid var(--border)' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: 600 }}>Oznacz jako zapłacony</h3>
        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '12px' }}>
          {koszt?.nazwa} — <strong>{formatujKwote(koszt?.kwota_brutto)}</strong>{koszt?.kontrahent ? ` · ${koszt.kontrahent}` : ''}
        </div>
        <form onSubmit={zapisz}>
          <div style={{ display: 'grid', gap: '8px', marginBottom: '12px' }}>
            {opcja('kw')}
            {opcja('konto')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>Data zapłaty</label>
              <input data-testid="zaplac-data" type="date" value={data} onChange={e => setData(e.target.value)} style={pole} required />
            </div>
            {sposob === 'kw' && (
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>Forma (KW)</label>
                <select data-testid="zaplac-typ" value={typPlatnosci} onChange={e => setTypPlatnosci(e.target.value)} style={pole}>
                  <option value="gotowka">gotówka</option>
                  <option value="przelew">przelew</option>
                  <option value="karta">karta</option>
                </select>
              </div>
            )}
          </div>
          {blad && <div data-testid="zaplac-blad" style={{ marginTop: '10px', fontSize: '11px', color: 'var(--danger)' }}>{blad}</div>}
          <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'flex-end' }}>
            <button type="button" data-testid="zaplac-anuluj" onClick={onZamknij} style={btn(false)}>Anuluj</button>
            <button type="submit" data-testid="zaplac-zapisz" disabled={wysylanie} style={{ ...btn(true), opacity: wysylanie ? 0.6 : 1 }}>{wysylanie ? 'Zapisywanie…' : 'Oznacz zapłacony'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
